/*
 * Firebase Cloud Messaging (Web) del dashboard: obtiene el token del navegador
 * para que el API EAD avise a MATRIZ cuando un chofer crea una solicitud de
 * devolucion (decision 2026-09-09: push, no polling).
 *
 * - Config publica en REACT_APP_FIREBASE_* (.env.*); sin ella todo es no-op.
 * - El service worker public/firebase-messaging-sw.js se registra a mano con la
 *   config en el query string (CRA no sustituye variables en public/).
 * - Requiere HTTPS (o localhost) y permiso de notificaciones del usuario.
 * - En primer plano el SDK entrega el mensaje a la pagina (onMessage); en
 *   segundo plano lo pinta el worker.
 */
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, deleteToken, onMessage, isSupported } from "firebase/messaging";

export const ACCION_SOLICITUD_DEVOLUCION = "solicitud_devolucion";

const CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const RUTA_SW = "/firebase-messaging-sw.js";

let messagingPromise = null;

export const pushConfigurado = () =>
  Boolean(CONFIG.apiKey && CONFIG.projectId && CONFIG.messagingSenderId && CONFIG.appId && VAPID_KEY);

// Instancia unica de Messaging (null si el navegador no soporta push o falta config).
const obtenerMessaging = () => {
  if (messagingPromise) {
    return messagingPromise;
  }
  messagingPromise = (async () => {
    if (!pushConfigurado() || typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return null;
    }
    if (!(await isSupported())) {
      return null;
    }
    const app = getApps().length ? getApps()[0] : initializeApp(CONFIG);
    return getMessaging(app);
  })().catch(function (error) {
    // eslint-disable-next-line no-console
    console.warn("[push] Firebase Messaging no disponible:", error && error.message);
    return null;
  });
  return messagingPromise;
};

// Registra el worker con la config en el query string; una misma URL (config
// identica) reutiliza el registro existente.
const registrarServiceWorker = async () => {
  const params = new URLSearchParams(CONFIG);
  const url = RUTA_SW + "?" + params.toString();
  const registro = await navigator.serviceWorker.register(url);
  await navigator.serviceWorker.ready;
  return registro;
};

// Estado del permiso de notificaciones: "granted" | "denied" | "default" | "unsupported"
export const estadoPermiso = () =>
  typeof window !== "undefined" && "Notification" in window ? window.Notification.permission : "unsupported";

/*
 * Pide permiso (si hace falta) y devuelve el token FCM Web del navegador, o null
 * si el usuario lo niega, el navegador no soporta push o falta configuracion.
 * `pedirPermiso=false` solo obtiene el token si el permiso YA fue concedido
 * (para no molestar en cada carga cuando el usuario ya dijo no).
 */
export const obtenerTokenWeb = async (pedirPermiso) => {
  const messaging = await obtenerMessaging();
  if (!messaging) {
    return null;
  }
  let permiso = estadoPermiso();
  if (permiso === "default" && pedirPermiso) {
    permiso = await window.Notification.requestPermission();
  }
  if (permiso !== "granted") {
    return null;
  }
  const registro = await registrarServiceWorker();
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registro });
  return token || null;
};

// Invalida el token en FCM (cierre de sesion): el API EAD lo purgara al recibir
// UNREGISTERED aunque el DELETE al API de logistica fallara.
export const borrarTokenWeb = async () => {
  try {
    const messaging = await obtenerMessaging();
    if (messaging) {
      await deleteToken(messaging);
    }
  } catch (error) {
    // best-effort
  }
};

/*
 * Mensajes en PRIMER PLANO (pestana visible). `callback(data)` recibe el `data`
 * del push del API EAD. Devuelve la funcion para cancelar la suscripcion (no-op
 * si no hay messaging).
 */
export const escucharMensajes = (callback) => {
  let cancelar = () => {};
  let cancelado = false;
  obtenerMessaging().then(function (messaging) {
    if (!messaging || cancelado) {
      return;
    }
    cancelar = onMessage(messaging, function (payload) {
      callback((payload && payload.data) || {});
    });
  });
  return () => {
    cancelado = true;
    cancelar();
  };
};
